import { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import {
  FaEdit,
  FaEye,
  FaTrash,
  FaSearch,
  FaFilter,
  FaRedoAlt,
} from "react-icons/fa";
import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "./deletepopup";
import Placeholder from "react-bootstrap/Placeholder";

import { getPlace } from "../../store/slices/place";

import {
  gettravelHotel,
  deletetravelHotel,
} from "../../store/slices/travelhotel";

export default function Hotels() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { travelHotelList = [] } = useSelector(
    (state) => state.travelHotel
  );

  /* =====================================================
     URL FILTER STATE
  ===================================================== */

  const [searchParams, setSearchParams] =
    useSearchParams();

  const searchName =
    searchParams.get("search") || "";

  const category =
    searchParams.get("category") || "";

  const location =
    searchParams.get("location") || "";

  const currentPage = Number(
    searchParams.get("page") || 1
  );

  const pageLimit = 9;

  /* =====================================================
     LOCAL STATE
  ===================================================== */

  const [loading, setLoading] = useState(true);

  const [showDelete, setShowDelete] =
    useState(false);

  const [selectedId, setSelectedId] =
    useState(null);

  const [deleteStatus, setDeleteStatus] =
    useState("confirm");

  const [places, setPlaces] = useState([]);

  /* =====================================================
     UPDATE URL FILTERS
  ===================================================== */

  const updateFilters = ({
    search = searchName,
    category: newCategory = category,
    location: newLocation = location,
    page = 1,
  }) => {
    const params = {};

    if (search) {
      params.search = search;
    }

    if (newCategory) {
      params.category = newCategory;
    }

    if (newLocation) {
      params.location = newLocation;
    }

    if (page > 1) {
      params.page = page;
    }

    setSearchParams(params);
  };

  /* =====================================================
     RESET FILTERS
  ===================================================== */

  const handleReset = () => {
    setSearchParams({});
  };

  /* =====================================================
     FETCH PLACES
  ===================================================== */

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const res = await dispatch(
          getPlace()
        ).unwrap();

        setPlaces(res?.places || []);
      } catch (error) {
        console.log(error);
      }
    };

    fetchPlaces();
  }, [dispatch]);

  /* =====================================================
     FETCH HOTELS
  ===================================================== */

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        setLoading(true);

        const params = {};

        if (searchName) {
          params.name = searchName;
        }

        if (category) {
          params.category = category;
        }

        if (location) {
          params.location = location;
        }

        await dispatch(
          gettravelHotel(params)
        ).unwrap();

      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, [
    dispatch,
    searchName,
    category,
    location,
  ]);

  /* =====================================================
     HOTEL LIST
  ===================================================== */

  const hotelList = Array.isArray(
    travelHotelList
  )
    ? travelHotelList
    : [];

  const totalPages = Math.ceil(
    hotelList.length / pageLimit
  );

  /*
   * If a filter reduces the results and the
   * current page no longer exists, go back
   * to page 1.
   */
  useEffect(() => {
    if (
      !loading &&
      totalPages > 0 &&
      currentPage > totalPages
    ) {
      updateFilters({
        page: 1,
      });
    }
  }, [
    loading,
    totalPages,
    currentPage,
  ]);

  const startIndex =
    (currentPage - 1) * pageLimit;

  const currentHotels = hotelList.slice(
    startIndex,
    startIndex + pageLimit
  );

  /* =====================================================
     PAGINATION
  ===================================================== */

  const changePage = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages
    ) {
      updateFilters({
        page: pageNumber,
      });
    }
  };

  /* =====================================================
     DELETE HOTEL
  ===================================================== */

  const handleConfirmDelete = async () => {
    try {
      setDeleteStatus("loading");

      await dispatch(
        deletetravelHotel(selectedId)
      ).unwrap();

      setDeleteStatus("success");

      setTimeout(async () => {
        setShowDelete(false);
        setDeleteStatus("confirm");

        /*
         * Refresh current hotel list
         */
        try {
          setLoading(true);

          const params = {};

          if (searchName) {
            params.name = searchName;
          }

          if (category) {
            params.category = category;
          }

          if (location) {
            params.location = location;
          }

          await dispatch(
            gettravelHotel(params)
          ).unwrap();

        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      }, 1000);

    } catch (error) {
      console.log(error);
      setDeleteStatus("confirm");
    }
  };

  /* =====================================================
     SKELETON
  ===================================================== */

  const SkeletonCard = () => (
    <Col
      lg={4}
      md={6}
      sm={12}
      className="mb-4"
    >
      <Card
        className="h-100 border-0"
        style={{
          borderRadius: "18px",
          overflow: "hidden",
        }}
      >
        <Placeholder
          as="div"
          animation="wave"
          style={{
            height: "200px",
            backgroundColor: "#e9ecef",
          }}
        />

        <Card.Body className="p-3">

          <Placeholder
            as="h6"
            animation="wave"
          >
            <Placeholder xs={8} />
          </Placeholder>

          <Placeholder
            as="p"
            animation="wave"
            className="mb-0"
          >
            <Placeholder xs={10} />
          </Placeholder>

        </Card.Body>
      </Card>
    </Col>
  );

  /* =====================================================
     UI
  ===================================================== */

  return (
    <>
      {/* ================= BREADCRUMB ================= */}

      <div className="breadcrumb-bar mb-2">

        <div className="breadcrumb-left">

          <Link
            to="/admin/home"
            className="breadcrumb-link active"
          >
            Home
          </Link>

          <span className="breadcrumb-separator">
            |
          </span>

          <span className="breadcrumb-link inactive">
            Hotels
          </span>

        </div>

        <button
          className="add-outlet-btn"
          onClick={() =>
            navigate("/admin/addhotel")
          }
        >
          + Add New
        </button>

      </div>


      {/* ================= FILTERS ================= */}

      <Row className="mb-3 p-3">

        {/* SEARCH */}

        <Col md={3}>

          <div className="position-relative">

            <FaSearch
              className="position-absolute"
              style={{
                left: "12px",
                top: "50%",
                transform:
                  "translateY(-50%)",
                color: "#999",
                fontSize: "12px",
                zIndex: 2,
                pointerEvents: "none",
              }}
            />

            <input
              type="text"
              className="form-control vendor-search-input"
              placeholder="Search hotel name"
              value={searchName}
              onChange={(e) =>
                updateFilters({
                  search: e.target.value,
                  page: 1,
                })
              }
              style={{
                paddingLeft: "32px",
              }}
            />

          </div>

        </Col>


        {/* CATEGORY */}

        <Col md={3}>

          <div className="position-relative">

            <FaFilter
              className="position-absolute"
              style={{
                left: "12px",
                top: "50%",
                transform:
                  "translateY(-50%)",
                color: "#999",
                fontSize: "11px",
                zIndex: 2,
                pointerEvents: "none",
              }}
            />

            <select
              className="form-select vendor-search-input"
              value={category}
              onChange={(e) =>
                updateFilters({
                  category:
                    e.target.value,
                  page: 1,
                })
              }
              style={{
                paddingLeft: "32px",
              }}
            >
              <option value="">
                All Category
              </option>

              <option value="Budget">
                Budget
              </option>

              <option value="2 Star">
                2 Star
              </option>

              <option value="3 Star">
                3 Star
              </option>

              <option value="4 Star">
                4 Star
              </option>

              <option value="5 Star">
                5 Star
              </option>

              <option value="Luxury">
                Luxury
              </option>

              <option value="Villa">
                Villa
              </option>

              <option value="Home Stay">
                Home Stay
              </option>

            </select>

          </div>

        </Col>


        {/* LOCATION */}

        <Col md={3}>

          <select
            className="form-select"
            value={location}
            onChange={(e) =>
              updateFilters({
                location:
                  e.target.value,
                page: 1,
              })
            }
          >
            <option value="">
              All Locations
            </option>

            {places.map((p) => (
              <option
                key={p._id}
                value={p._id}
              >
                {p.name}
              </option>
            ))}

          </select>

        </Col>


        {/* RESET */}

        <Col md={3}>

          <button
            className="cancel-btn w-75"
            onClick={handleReset}
          >
            <FaRedoAlt
              size={10}
              className="me-1"
            />

            Reset Filters
          </button>

        </Col>

      </Row>


      {/* ================= HOTEL CARDS ================= */}

      <Container className="mb-5">

        <Row>

          {loading ? (

            Array.from({
              length: 6,
            }).map((_, index) => (
              <SkeletonCard
                key={index}
              />
            ))

          ) : currentHotels.length > 0 ? (

            currentHotels.map(
              (hotel) => (

                <Col
                  lg={4}
                  md={6}
                  sm={12}
                  key={hotel._id}
                  className="mb-4"
                >

                  <Card
                    className="h-100 hotel-card border-0"
                    style={{
                      borderRadius:
                        "18px",
                      overflow:
                        "hidden",
                      transition:
                        "all 0.3s ease",
                    }}
                  >

                    {/* IMAGE */}

                    <div className="hotel-img-wrapper">

                      <Card.Img
                        src={
                          hotel.image
                        }
                        alt={
                          hotel.name
                        }
                        className="hotel-img"
                      />


                      {/* ACTIONS */}

                      <div className="hotel-actions">

                        <FaEye
                          size={25}
                          style={{
                            color:
                              "green",
                            cursor:
                              "pointer",
                          }}
                          onClick={() =>
                            navigate(
                              `/admin/hoteldetails/${hotel._id}`
                            )
                          }
                        />

                        <FaEdit
                          size={25}
                          style={{
                            color:
                              "blue",
                            cursor:
                              "pointer",
                          }}
                          onClick={() =>
                            navigate(
                              `/admin/edithotel/${hotel._id}`
                            )
                          }
                        />

                        <FaTrash
                          size={25}
                          style={{
                            color:
                              "red",
                            cursor:
                              "pointer",
                          }}
                          onClick={() => {
                            setSelectedId(
                              hotel._id
                            );

                            setShowDelete(
                              true
                            );
                          }}
                        />

                      </div>

                    </div>


                    {/* BODY */}

                    <Card.Body className="p-3">

                      <span
                        style={{
                          cursor:
                            "pointer",
                        }}
                        className="text-decoration-none text-dark"
                        onClick={() =>
                          navigate(
                            `/admin/hoteldetails/${hotel._id}`
                          )
                        }
                      >

                        <h6 className="fw-semibold mb-1 hotel-title">
                          {hotel.name}
                        </h6>

                      </span>


                      <p className="text-muted mb-0 hotel-address">
                        {hotel.location
                          ?.name ||
                          "-"}
                      </p>

                      <p className="text-muted mb-0 hotel-address">
                        {hotel?.category ||
                          "-"}
                      </p>

                    </Card.Body>

                  </Card>

                </Col>

              )
            )

          ) : (

            <Col xs={12}>

              <div className="text-center py-5 text-muted">

                <h6>
                  No hotels found
                </h6>

                <p className="small mb-3">
                  Try changing your
                  search or filters.
                </p>

                {(searchName ||
                  category ||
                  location) && (
                    <button
                      className="cancel-btn"
                      onClick={
                        handleReset
                      }
                    >
                      Reset Filters
                    </button>
                  )}

              </div>

            </Col>

          )}

        </Row>


        {/* ================= PAGINATION ================= */}

        {!loading &&
          totalPages > 1 && (

            <div className="d-flex justify-content-center align-items-center gap-2 mt-4 flex-wrap">

              <Button
                variant="light"
                disabled={
                  currentPage === 1
                }
                onClick={() =>
                  changePage(
                    currentPage - 1
                  )
                }
              >
                ‹
              </Button>


              {Array.from(
                {
                  length:
                    totalPages,
                },
                (_, i) => i + 1
              )
                .filter((page) => {

                  return (
                    page === 1 ||
                    page ===
                    totalPages ||
                    Math.abs(
                      page -
                      currentPage
                    ) <= 1
                  );

                })
                .map(
                  (
                    page,
                    index,
                    arr
                  ) => {

                    const prevPage =
                      arr[index - 1];

                    return (
                      <div
                        key={page}
                        className="d-flex align-items-center gap-2"
                      >

                        {prevPage &&
                          page -
                          prevPage >
                          1 && (
                            <span>
                              ...
                            </span>
                          )}

                        <Button
                          variant={
                            currentPage ===
                              page
                              ? "dark"
                              : "light"
                          }
                          onClick={() =>
                            changePage(
                              page
                            )
                          }
                        >
                          {page}
                        </Button>

                      </div>
                    );

                  }
                )}


              <Button
                variant="light"
                disabled={
                  currentPage ===
                  totalPages
                }
                onClick={() =>
                  changePage(
                    currentPage + 1
                  )
                }
              >
                ›
              </Button>

            </div>

          )}

      </Container>


      {/* ================= DELETE MODAL ================= */}

      <DeleteModal
        show={showDelete}
        onHide={() => {
          setShowDelete(false);
          setDeleteStatus(
            "confirm"
          );
        }}
        onConfirm={
          handleConfirmDelete
        }
        status={deleteStatus}
      />

    </>
  );
}